import { useCallback, useEffect, useRef, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import {
  INTEGRATION_SLOT_ITEM_CODE,
  type MutationResult,
  type SlotEntitlement,
  type Subscription,
  type SubscriptionSummary,
} from "../types/billing";

// Per-tenant billing for the instance-detail Billing panel: the tenant's
// subscription (with line items) plus its computed slot entitlement, and the
// mutations that drive them. Mutations change Stripe on the backend; we reload
// afterwards so the panel reflects the re-synced mirror (Stripe stays the source
// of truth). Manual fetch/reload pattern, mirroring useAdminChannels.

const subUrl = (id: string): string => `/api/admin/billing/subscriptions/${encodeURIComponent(id)}`;
const entitlementUrl = (tenantId: string): string =>
  `/api/admin/billing/tenants/${encodeURIComponent(tenantId)}/entitlement`;
const mutationHeaders = (): Record<string, string> => ({ "Content-Type": "application/json", ...getCsrfHeader() });

export type TenantBillingStatus = "loading" | "ready" | "error";

export type UseTenantBillingResult = {
  readonly status: TenantBillingStatus;
  readonly subscription: Subscription | null;
  readonly entitlement: SlotEntitlement | null;
  readonly error: string | null;
  readonly reload: () => void;
  readonly addSlot: (quantity?: number) => Promise<MutationResult>;
  readonly changeItemQuantity: (stripeItemId: string, quantity: number) => Promise<MutationResult>;
  readonly removeItem: (stripeItemId: string) => Promise<MutationResult>;
  readonly setTier: (tier: string, interval: string) => Promise<MutationResult>;
  readonly sync: () => Promise<MutationResult>;
  readonly openPortal: () => Promise<{ ok: true; url: string } | { ok: false; message: string }>;
};

const failure = async (res: Response, fallback: string): Promise<{ ok: false; message: string }> => {
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  return { ok: false, message: body?.message ?? `${fallback} (${res.status})` };
};

export const useTenantBilling = (tenantId: string): UseTenantBillingResult => {
  const [status, setStatus] = useState<TenantBillingStatus>("loading");
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [entitlement, setEntitlement] = useState<SlotEntitlement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The current subscription id, kept in a ref so mutation callbacks stay
  // stable and always target the loaded subscription.
  const subscriptionIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const opts = { credentials: "include" as const, signal: controller.signal };
    try {
      const [entRes, listRes] = await Promise.all([
        fetch(entitlementUrl(tenantId), opts),
        fetch(`/api/admin/billing/subscriptions?tenantId=${encodeURIComponent(tenantId)}&limit=1`, opts),
      ]);
      if (!entRes.ok) throw new Error(`entitlement failed (${entRes.status})`);
      if (!listRes.ok) throw new Error(`subscription lookup failed (${listRes.status})`);
      const ent = (await entRes.json()) as SlotEntitlement;
      const summaries = (await listRes.json()) as SubscriptionSummary[];

      let sub: Subscription | null = null;
      const summary = summaries[0];
      if (summary) {
        const detailRes = await fetch(subUrl(summary.stripeSubscriptionId), opts);
        if (!detailRes.ok) throw new Error(`subscription detail failed (${detailRes.status})`);
        sub = (await detailRes.json()) as Subscription;
      }
      if (controller.signal.aborted) return;
      subscriptionIdRef.current = sub?.stripeSubscriptionId ?? null;
      setEntitlement(ent);
      setSubscription(sub);
      setError(null);
      setStatus("ready");
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  // Runs a subscription mutation against the loaded subscription, then reloads
  // so the panel shows the re-synced state. Returns a typed ok/message result.
  const runMutation = useCallback(
    async (path: string, method: string, body?: unknown): Promise<MutationResult> => {
      const id = subscriptionIdRef.current;
      if (id === null) return { ok: false, message: "no subscription for this tenant" };
      const res = await fetch(`${subUrl(id)}${path}`, {
        method,
        credentials: "include",
        headers: mutationHeaders(),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      if (!res.ok) return failure(res, "request failed");
      await load();
      return { ok: true };
    },
    [load],
  );

  const addSlot = useCallback(
    (quantity = 1): Promise<MutationResult> =>
      runMutation("/items", "POST", { itemCode: INTEGRATION_SLOT_ITEM_CODE, quantity }),
    [runMutation],
  );

  const changeItemQuantity = useCallback(
    (stripeItemId: string, quantity: number): Promise<MutationResult> =>
      runMutation(`/items/${encodeURIComponent(stripeItemId)}`, "PATCH", { quantity }),
    [runMutation],
  );

  const removeItem = useCallback(
    (stripeItemId: string): Promise<MutationResult> =>
      runMutation(`/items/${encodeURIComponent(stripeItemId)}`, "DELETE"),
    [runMutation],
  );

  const setTier = useCallback(
    (tier: string, interval: string): Promise<MutationResult> => runMutation("/tier", "PUT", { tier, interval }),
    [runMutation],
  );

  const sync = useCallback((): Promise<MutationResult> => runMutation("/sync", "POST"), [runMutation]);

  const openPortal = useCallback(async (): Promise<
    { ok: true; url: string } | { ok: false; message: string }
  > => {
    const id = subscriptionIdRef.current;
    if (id === null) return { ok: false, message: "no subscription for this tenant" };
    const res = await fetch(`${subUrl(id)}/portal-session`, {
      method: "POST",
      credentials: "include",
      headers: getCsrfHeader(),
    });
    if (!res.ok) return failure(res, "portal session failed");
    const data = (await res.json()) as { url: string };
    return { ok: true, url: data.url };
  }, []);

  return {
    status,
    subscription,
    entitlement,
    error,
    reload: () => void load(),
    addSlot,
    changeItemQuantity,
    removeItem,
    setTier,
    sync,
    openPortal,
  };
};
